import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Alert,
    Box,
    Typography,
} from "@mui/material";
import { runInAction } from "mobx";
import { apiService } from "../services/api";
import { authStore } from "../stores/authStore";

interface ChangePasswordDialogProps {
    open: boolean;
    onClose: () => void;
    required?: boolean; // Если true, нельзя закрыть диалог без смены пароля
}

const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({ open, onClose, required = false }) => {
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        // Если required (mustChangePassword = true), текущий пароль не нужен
        if (!required && !currentPassword) {
            setError("Текущий пароль обязателен");
            return;
        }

        if (!newPassword || !confirmPassword) {
            setError("Новый пароль и подтверждение обязательны");
            return;
        }

        if (newPassword.length < 6) {
            setError("Новый пароль должен содержать минимум 6 символов");
            return;
        }

        if (newPassword !== confirmPassword) {
            setError("Новые пароли не совпадают");
            return;
        }

        setLoading(true);
        try {
            // Если required, не передаем currentPassword
            await apiService.changePassword(required ? undefined : currentPassword, newPassword);
            
            // Обновляем данные пользователя, чтобы сбросить mustChangePassword
            const updatedUser = await apiService.getCurrentUser();
            runInAction(() => {
                authStore.user = updatedUser;
            });
            localStorage.setItem("user", JSON.stringify(updatedUser));
            
            // Очищаем форму
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
            
            onClose();
        } catch (err: any) {
            setError(err.response?.data?.message || "Ошибка смены пароля");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (required) {
            return; // Нельзя закрыть, если требуется смена пароля
        }
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setError(null);
        onClose();
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown={required}
        >
            <form onSubmit={handleSubmit}>
                <DialogTitle>
                    {required ? "Требуется смена пароля" : "Смена пароля"}
                </DialogTitle>
                <DialogContent>
                    {required && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                Для безопасности вашей учетной записи необходимо сменить пароль при первом входе.
                            </Typography>
                        </Alert>
                    )}
                    
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
                        {!required && (
                            <TextField
                                label="Текущий пароль"
                                type="password"
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                required
                                fullWidth
                                disabled={loading}
                                autoFocus
                            />
                        )}
                        <TextField
                            label="Новый пароль"
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                            fullWidth
                            disabled={loading}
                            helperText="Минимум 6 символов"
                            autoFocus={required}
                        />
                        <TextField
                            label="Подтвердите новый пароль"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            fullWidth
                            disabled={loading}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    {!required && (
                        <Button onClick={handleClose} disabled={loading}>
                            Отмена
                        </Button>
                    )}
                    <Button 
                        type="submit" 
                        variant="contained" 
                        color="primary"
                        disabled={loading || (!required && !currentPassword) || !newPassword || !confirmPassword}
                    >
                        {loading ? "Сохранение..." : "Сменить пароль"}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default ChangePasswordDialog;

